import { useMemo } from 'react'

import { useIncidentDescription } from '../../../shared/hooks/useIncidentDescription'
import { useUpdateIncidentPriority } from '../../../shared/hooks/useUpdateIncidentPriority'
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlags,
} from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { trackEvent } from '@/utils/analytics/useAnalytics'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  IncidentCard,
  IncidentCardDescriptionChip,
  IncidentCardDescriptionText,
  RecordedFormResponses,
} from '@hivewatch/dark'
import type { ColorScheme, IncidentCardPriority } from '@hivewatch/dark'

import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentPriority, IncidentSource } from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import {
  closeIncidentMapPanelDrawer,
  closeSOPIncidentDrawer,
  closeWorkflowDrawer,
  toggleIncidentTraceLogDrawer,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'
import {
  formatDateTime,
  formatDateTimeInTimeZone,
} from '@/utils/formatDateTime'
import { creatorRoleName, formatFullName } from '@/utils/formats'
import { tVar } from '@/utils/i18n'
import { getDetectionTypeIcon } from '@/utils/incidentIcons'
import {
  getEmailIncidentSourceLabel,
  isCustomQuickReportIncident,
  isEmailSourcedIncident,
  isIngestedEmailIncident,
  isManualIncident,
  toCardPriority,
  toCardStatus,
} from '@/utils/incidents'

import { MixpanelDataIProps } from '../../../shared/types/types'
import {
  getDescriptionMessage,
  shouldShowTraceLogs,
} from '../../../shared/utils/feedUtils'
import { formatQuickReportDisplayAnswers } from '../../../shared/utils/quickReportAnswers'
import {
  VIEW_TL_EVENT,
  getIsTailgatingIncident,
  getMixpanelEventData,
} from '../../../shared/utils/traceLogUtils'
import { FeedButtons } from './FeedButtons'
import { OriginalEmail } from './OriginalEmail'
import { QuickReportDetail } from './QuickReportDetail'
import { ReasoningTrailCard } from './reasoning-trail/ReasoningTrailCard'

function fromCardPriority(
  cardPriority: IncidentCardPriority
): IncidentPriority | null {
  switch (cardPriority) {
    case 'low':
      return IncidentPriority.Low
    case 'medium':
      return IncidentPriority.Medium
    case 'high':
      return IncidentPriority.High
    case 'urgent':
      return IncidentPriority.Critical
    default:
      return null
  }
}

// ─── Description builder ──────────────────────────────────────────────────────

interface BuildDescriptionIProps {
  colorScheme: ColorScheme
  device: IncidentQuery['incident']['devices'][0] | undefined
  incident: IncidentQuery['incident'] | undefined
  isEmailIncident: boolean
  isIncidentTypeDefinition: boolean
  /** Created from an Email Source; the email itself shows below the card. */
  isIngestedEmail: boolean
  /** See isCustomQuickReportIncident: the description stays out of the card. */
  isCustomQuickReport: boolean
  isManual: boolean
  /** Undefined when the device type has no drawer — chip stays non-interactive. */
  onDeviceClick?: () => void
  onFloorClick: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>
}

function buildDescription({
  colorScheme,
  device,
  incident,
  isCustomQuickReport,
  isEmailIncident,
  isIncidentTypeDefinition,
  isIngestedEmail,
  isManual,
  onDeviceClick,
  onFloorClick,
  t,
}: BuildDescriptionIProps): React.ReactNode {
  // The Email Source's current name, not the one it had when the email came in.
  if (isIngestedEmail) {
    const sourceName = incident?.email?.emailSourceName
    return sourceName ? (
      <IncidentCardDescriptionChip colorScheme={colorScheme}>
        {tVar(t, 'incidents:original_email_source_chip', {
          name: sourceName,
        })}
      </IncidentCardDescriptionChip>
    ) : null
  }

  // Email-generated incidents show the ingested alert email verbatim,
  // headers and all, instead of the "created from <device> on <floor>"
  // template (they have no device and no floor).
  if (isEmailIncident) {
    return (
      <IncidentCardDescriptionText
        colorScheme={colorScheme}
        overflowWrap='anywhere'
        whiteSpace='pre-line'
      >
        {incident?.description}
      </IncidentCardDescriptionText>
    )
  }

  const isTailgating = getIsTailgatingIncident(
    incident,
    isIncidentTypeDefinition
  )

  if (isTailgating) {
    return (
      <IncidentCardDescriptionText colorScheme={colorScheme}>
        {getDescriptionMessage(incident?.type)}
      </IncidentCardDescriptionText>
    )
  }

  if (isManual) {
    const creator = incident?.creator
    const categoryName = incident?.manualIncidentCategory?.name
    return (
      <>
        {!isCustomQuickReport && incident?.description && (
          <IncidentCardDescriptionText
            colorScheme={colorScheme}
            overflowWrap='anywhere'
            whiteSpace='pre-line'
          >
            {incident.description}
          </IncidentCardDescriptionText>
        )}
        {creator && (
          <>
            <IncidentCardDescriptionText colorScheme={colorScheme}>
              {t('incidents:incident_card_reporting_person')}
            </IncidentCardDescriptionText>
            <IncidentCardDescriptionChip colorScheme={colorScheme}>
              {`${creatorRoleName(creator.roleName)} ${formatFullName(
                creator
              )}`}
            </IncidentCardDescriptionChip>
          </>
        )}
        {categoryName && (
          <>
            <IncidentCardDescriptionText colorScheme={colorScheme}>
              {t('incidents:incident_card_incident_category')}
            </IncidentCardDescriptionText>
            <IncidentCardDescriptionChip colorScheme={colorScheme}>
              {categoryName}
            </IncidentCardDescriptionChip>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <IncidentCardDescriptionText colorScheme={colorScheme}>
        {tVar(t, 'incidents:incident_card_created_from', {
          name: incident?.name,
        })}
      </IncidentCardDescriptionText>
      {device?.name &&
        (onDeviceClick ? (
          <IncidentCardDescriptionChip
            colorScheme={colorScheme}
            onClick={onDeviceClick}
          >
            {device.name}
          </IncidentCardDescriptionChip>
        ) : (
          <IncidentCardDescriptionText colorScheme={colorScheme}>
            {device.name}
          </IncidentCardDescriptionText>
        ))}
      {incident?.floor?.name && (
        <>
          <IncidentCardDescriptionText colorScheme={colorScheme}>
            {t('incidents:incident_card_on')}
          </IncidentCardDescriptionText>
          <IncidentCardDescriptionChip
            colorScheme={colorScheme}
            onClick={onFloorClick}
          >
            {incident.floor.name}
          </IncidentCardDescriptionChip>
        </>
      )}
    </>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface IncidentDetailsIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
}

export const IncidentDetails = ({
  incident,
  mixpanelData,
}: IncidentDetailsIProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const colorScheme = useLightMode()
  const device = incident?.devices[0]
  const source = incident?.source
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const isSalesDatabaseReset = useFeatureFlag(FeatureFlag.salesDatabaseReset)
  const { useIncidentTypeDefinition: isIncidentTypeDefinition } =
    useFeatureFlags()
  // Email-generated incidents (Sensoscientific, Honeywell Mass Comms) get a
  // dedicated treatment: source name as the card title and the raw alert
  // email as the body. Gated behind the sales database reset flag until it
  // graduates beyond the sales environment.
  const isEmailIncident =
    isSalesDatabaseReset && isEmailSourcedIncident(incident?.source)
  const isIngestedEmail = isIngestedEmailIncident(incident)
  const slug = isIncidentTypeDefinition ? incident?.incidentType?.slug : null
  const isManual = isManualIncident({
    incidentType: incident?.incidentType,
    manualIncidentCategory: incident?.manualIncidentCategory,
  })
  const { shouldEnableIncidentActions } = usePermissions()
  const { updatePriority } = useUpdateIncidentPriority({
    priority: incident?.priority,
  })

  const handlePriorityChange = (cardPriority: IncidentCardPriority) => {
    const graphqlPriority = fromCardPriority(cardPriority)
    // Gate and report against incident?.priority — the value this card
    // renders — so re-selecting the displayed priority is a no-op and
    // oldValue is what the operator saw. (The hook's currentPriority is
    // seeded once on mount and can be stale here: this card mounts before
    // the incident loads and stays mounted across incident switches.)
    // Property names stay camelCase to match the legacy event — Mixpanel
    // reports keyed on newValue/oldValue must not split by flag state.
    if (incident?.id && graphqlPriority !== incident.priority) {
      trackEvent(`${BUTTON_PRESS} Manually set Incident Priority`, {
        newValue: graphqlPriority,
        oldValue: incident.priority,
        incidentId: incident.id,
        incidentDisplayId: incident?.displayId,
      })
      updatePriority(incident.id, graphqlPriority)
    }
  }

  const openTraceLogDrawer = () => {
    dispatch(closeSOPIncidentDrawer())
    dispatch(closeWorkflowDrawer())
    dispatch(closeIncidentMapPanelDrawer())
    dispatch(toggleIncidentTraceLogDrawer())
    const data = getMixpanelEventData(incident)
    trackEvent(`${BUTTON_PRESS} ${VIEW_TL_EVENT}`, data)
  }

  const facilityLocalCreatedAt = formatDateTimeInTimeZone(
    incident?.createdAt,
    incident?.facility?.timeZone
  )
  const timestamp = incident?.createdAt
    ? `${formatDateTime(incident.createdAt)}${
        facilityLocalCreatedAt
          ? ` (${facilityLocalCreatedAt} ${t(
              'incidents:incident_drawer_facility_local_time'
            )})`
          : ''
      }`
    : undefined

  const detectionTypeIcon = getDetectionTypeIcon(
    incident?.type,
    slug,
    isManual,
    colorScheme,
    incident?.name,
    device?.type,
    source,
    incident?.creator?.roleName
  )

  const shouldShowTraceLogsButton =
    isTraceLogsEnabled && shouldShowTraceLogs(incident)

  const isQuickReportIncident =
    source === IncidentSource.QuickReport ||
    source === IncidentSource.CustomQuickReport
  // Custom quick reports carry structured displayAnswers; canned ones only
  // have the legacy externalDetail text.
  const isCustomQuickReport = isCustomQuickReportIncident(incident)
  // Stable reference: RecordedFormResponses keys its ResizeObserver effect on
  // the answers array identity.
  const displayAnswers = incident?.quickReportDetails?.displayAnswers
  const formattedAnswers = useMemo(
    () => formatQuickReportDisplayAnswers(displayAnswers ?? []),
    [displayAnswers]
  )

  const { onDeviceClick, onFloorClick } = useIncidentDescription({
    deviceId: device?.id,
    deviceType: device?.type,
    floorId: incident?.floor?.id,
    type: incident?.type,
  })

  const description = buildDescription({
    colorScheme,
    device,
    incident,
    isCustomQuickReport,
    isEmailIncident,
    isIncidentTypeDefinition,
    isIngestedEmail,
    isManual,
    onDeviceClick,
    onFloorClick,
    t,
  })

  return (
    <>
      <IncidentCard
        aiOperatorUsed={incident?.wasProcessedByAiOperator ?? false}
        colorScheme={colorScheme}
        description={description}
        detectionTypeIcon={detectionTypeIcon}
        onPriorityChange={
          shouldEnableIncidentActions ? handlePriorityChange : undefined
        }
        onViewTraceLogs={
          shouldShowTraceLogsButton ? openTraceLogDrawer : undefined
        }
        priority={toCardPriority(incident?.priority)}
        status={toCardStatus(incident?.status)}
        timestamp={timestamp}
        title={
          (isEmailIncident
            ? getEmailIncidentSourceLabel(incident?.source)
            : incident?.name) ?? ''
        }
      >
        {isQuickReportIncident &&
          (isCustomQuickReport ? (
            <RecordedFormResponses
              answers={formattedAnswers}
              colorScheme={colorScheme}
              defaultSectionLabel={t(
                'incidents:quick_report_custom_general_section'
              )}
              size='sm'
            />
          ) : (
            <QuickReportDetail detail={incident?.externalDetail} />
          ))}
      </IncidentCard>

      <FeedButtons incident={incident} mixpanelData={mixpanelData} />

      {/* After the acknowledge buttons, so a long email never pushes them down. */}
      {isIngestedEmail && (
        <OriginalEmail
          email={incident?.email ?? null}
          facilityTimeZone={incident.facility?.timeZone}
        />
      )}

      <ReasoningTrailCard
        incidentId={incident?.id}
        wasProcessedByAiOperator={incident?.wasProcessedByAiOperator}
      />
    </>
  )
}
